// ---------------------------------------------------------------------------
// moderationService — denúncia e bloqueio de UGC.
//
// Exigência da política de UGC do Google Play (Teste Aberto): o app precisa
// oferecer, in-app, um caminho para DENUNCIAR conteúdo/usuário e outro para
// BLOQUEAR usuário.
//
// Fila separada da de catálogo (`catalogReportService`) de propósito: lá o
// operador conserta um livro, aqui avalia uma conta. Ver a justificativa
// completa em `functions/src/moderation/reportContent.ts`.
// ---------------------------------------------------------------------------

import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";

/**
 * Espelho de `REPORT_TARGET_TYPES` em `functions/src/shared/schemas.ts`.
 * `note` = nota pública do mural; `comment` fica só pelas denúncias antigas
 * (o mural gravava como comentário antes de 18/08).
 */
export type ReportTargetType = "post" | "review" | "comment" | "note" | "user";

export type ReportReason =
  | "spam"
  | "assedio"
  | "conteudo_inapropriado"
  | "discurso_odio"
  | "outro";

export interface ReportContentInput {
  targetType: ReportTargetType;
  targetId: string;
  reason: ReportReason;
  details?: string | null;
}

export interface ReportContentResult {
  ok: boolean;
  id: string;
  /** true quando a denúncia já existia e foi atualizada (não duplicou). */
  updated: boolean;
}

export interface BlockUserResult {
  ok: boolean;
  blockedUid: string;
  /**
   * O bloqueio desfez o follow nos dois sentidos. Os dois campos vêm do
   * servidor porque só ele sabe se a aresta existia — em especial a de
   * `removedFollower`, que vive num doc que o client nunca leu (quem me segue
   * não está em lugar nenhum do estado local).
   *
   * ⚠️ Toleram `undefined`: a callable antiga não devolvia nenhum dos dois, e
   * a janela hosting-antes-de-functions existe. Quem lê trata ausência como
   * "não sei", nunca como `false`.
   */
  unfollowed?: boolean;
  removedFollower?: boolean;
}

export interface UnblockUserResult {
  ok: boolean;
  blockedUid: string;
}

export interface RemoveFollowerResult {
  ok: boolean;
  targetUid: string;
  /** `false` = a pessoa já não te seguia (sucesso no-op, não erro). */
  removed: boolean;
}

/**
 * Id de uma resenha para fins de denúncia. A resenha vive em
 * `book_reviews/{bookId}/reviews/{authorUid}`, então o par identifica o doc —
 * e é o que deixa o admin navegar até ela. `::` como separador porque `_`
 * aparece dentro de uid e `:` aparece dentro de bookId (`apple:123`), então
 * nenhum dos dois sozinho seria reversível; o parser do servidor usa o
 * ÚLTIMO `::`, o que mantém a volta correta mesmo se o bookId contiver um.
 */
export function reviewTargetId(bookId: string, authorUid: string): string {
  return `${bookId}::${authorUid}`;
}

// Mesmo padrão de região do catalogReportService.
const functionsRegion = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as string | undefined;

function getFunctionsInstance() {
  const app = getApp();
  return functionsRegion?.trim() ? getFunctions(app, functionsRegion.trim()) : getFunctions(app);
}

export async function reportContent(input: ReportContentInput): Promise<ReportContentResult> {
  const callable = httpsCallable<ReportContentInput, ReportContentResult>(
    getFunctionsInstance(),
    "reportContent",
  );
  const res = await callable(input);
  return res.data;
}

export async function blockUser(targetUid: string): Promise<BlockUserResult> {
  const callable = httpsCallable<{ targetUid: string }, BlockUserResult>(
    getFunctionsInstance(),
    "blockUser",
  );
  const res = await callable({ targetUid });
  return res.data;
}

export async function unblockUser(targetUid: string): Promise<UnblockUserResult> {
  const callable = httpsCallable<{ targetUid: string }, UnblockUserResult>(
    getFunctionsInstance(),
    "unblockUser",
  );
  const res = await callable({ targetUid });
  return res.data;
}

/**
 * "Remover seguidor" sem bloquear — `docs/systems/remove-follower.md`. Callable
 * porque as rules só deixam apagar a aresta quem SEGUE, e o contador
 * `followingCount` de quem é removido não é gravável por terceiros.
 */
export async function removeFollower(targetUid: string): Promise<RemoveFollowerResult> {
  const callable = httpsCallable<{ targetUid: string }, RemoveFollowerResult>(
    getFunctionsInstance(),
    "removeFollower",
  );
  const res = await callable({ targetUid });
  return res.data;
}
